import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Button, Icon, Message, Progress, Input } from "semantic-ui-react";
import { SG_REPLACE_DEBRIEF_PDF_FILE, RESET_DEBRIEF_PDF_REPORT, SG_FETCH_DEBRIEF_PDF_REPORT } from "constants/actions";

const ReplacePdfFile = ({ templateId, currentFile, onUploadSuccess }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [customFileName, setCustomFileName] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { pending, updated } = useSelector(
    (state) => ({
      pending: state.debrief_pdf_report?.pending,
      updated: state.debrief_pdf_report?.updated,
    }),
    shallowEqual
  );

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "application/pdf") {
        setSelectedFile(file);
      } else {
        alert("Please select a PDF file");
        event.target.value = null;
      }
    }
  };

  const handleUpload = () => {
    if (!selectedFile || !templateId) return;

    const formData = new FormData();
    
    // Create a new file with custom name if provided
    if (customFileName && customFileName.trim()) {
      const fileExtension = selectedFile.name.split('.').pop();
      const newFileName = customFileName.trim().endsWith(`.${fileExtension}`) 
        ? customFileName.trim() 
        : `${customFileName.trim()}.${fileExtension}`;
      const renamedFile = new File([selectedFile], newFileName, { type: selectedFile.type });
      formData.append("file", renamedFile);
    } else {
      formData.append("file", selectedFile);
    }

    setUploading(true);
    setUploadProgress(0);
    setShowSuccess(false);

    // Simulate progress (in real scenario, you'd track actual upload progress)
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    dispatch({
      type: RESET_DEBRIEF_PDF_REPORT,
    });

    dispatch({
      type: SG_REPLACE_DEBRIEF_PDF_FILE,
      payload: {
        id: templateId,
        formData: formData,
      },
    });
  };

  const handleReset = () => {
    setSelectedFile(null);
    setCustomFileName("");
    setUploadProgress(0);
    setUploading(false);
    setShowSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  React.useEffect(() => {
    if (updated && uploading) {
      setUploadProgress(100);
      setTimeout(() => {
        setUploading(false);
        setShowSuccess(true);
        handleReset();
        
        // Refresh the data after successful upload
        if (templateId) {
          dispatch({
            type: SG_FETCH_DEBRIEF_PDF_REPORT,
            payload: { id: templateId },
          });
        }
        
        // Call parent callback if provided
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }, 1000);
    }
  }, [updated, uploading, templateId, dispatch, onUploadSuccess]);

  return (
    <Container>
      <Header>
        <Icon name="file pdf outline" />
        {currentFile ? "Replace PDF File" : "Upload PDF File"}
      </Header>

      {currentFile && (
        <CurrentFileInfo>
          <InfoLabel>Current File:</InfoLabel>
          <FileName>{currentFile.file_name || "Unnamed PDF"}</FileName>
        </CurrentFileInfo>
      )}

      <UploadSection>
        <FileInputWrapper>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            disabled={uploading || pending}
          />
          <Button
            icon
            labelPosition="left"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || pending}
            color="blue"
          >
            <Icon name="file" />
            Select PDF File
          </Button>
        </FileInputWrapper>

        {selectedFile && (
          <CustomFileNameWrapper>
            <Input
              fluid
              placeholder="Custom file name (optional - leave blank to use uploaded name + timestamp)"
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              disabled={uploading || pending}
              icon="edit"
              iconPosition="left"
            />
            <HelpText>The file extension will be added automatically</HelpText>
          </CustomFileNameWrapper>
        )}

        {selectedFile && (
          <SelectedFileInfo>
            <FileDetails>
              <Icon name="file pdf outline" color="red" size="large" />
              <FileInfoText>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</FileSize>
              </FileInfoText>
            </FileDetails>
            {!uploading && (
              <ActionButtons>
                <Button
                  color="green"
                  onClick={handleUpload}
                  disabled={pending}
                  loading={pending}
                >
                  <Icon name="upload" />
                  {currentFile ? "Replace" : "Upload"}
                </Button>
                <Button onClick={handleReset} disabled={pending}>
                  <Icon name="cancel" />
                  Cancel
                </Button>
              </ActionButtons>
            )}
          </SelectedFileInfo>
        )}

        {uploading && (
          <ProgressWrapper>
            <Progress percent={uploadProgress} indicating progress>
              Uploading...
            </Progress>
          </ProgressWrapper>
        )}

        {showSuccess && (
          <Message positive onDismiss={() => setShowSuccess(false)}>
            <Message.Header>Success!</Message.Header>
            <p>PDF file has been {currentFile ? "replaced" : "uploaded"} successfully. The page data has been refreshed.</p>
          </Message>
        )}
      </UploadSection>

      <InfoMessage info>
        <Message.Header>File Requirements</Message.Header>
        <Message.List>
          <Message.Item>Only PDF files are accepted</Message.Item>
          <Message.Item>Maximum file size: 50 MB</Message.Item>
          <Message.Item>
            {currentFile
              ? "The current file will be replaced with the new one"
              : "A new PDF file will be added to this report"}
          </Message.Item>
        </Message.List>
      </InfoMessage>
    </Container>
  );
};

export default ReplacePdfFile;

const Container = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const Header = styled.h3`
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.2em;
  color: #1f2937;
`;

const CurrentFileInfo = styled.div`
  padding: 12px;
  background: #ffffff;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #6b7280;
  margin-right: 8px;
`;

const UploadSection = styled.div`
  margin-top: 15px;
`;

const FileInputWrapper = styled.div`
  margin-bottom: 15px;
`;

const CustomFileNameWrapper = styled.div`
  margin-bottom: 15px;
`;

const HelpText = styled.div`
  font-size: 0.875em;
  color: #6b7280;
  margin-top: 6px;
  font-style: italic;
`;

const HiddenInput = styled.input`
  display: none;
`;

const SelectedFileInfo = styled.div`
  padding: 15px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-top: 15px;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
`;

const FileInfoText = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const FileSize = styled.div`
  font-size: 0.875em;
  color: #6b7280;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ProgressWrapper = styled.div`
  margin-top: 15px;
`;

const InfoMessage = styled(Message)`
  margin-top: 20px !important;
`;
